import type { ReactNode } from "react";
import { Button } from "./button";
import { cn } from "./cn";
import { Icon } from "./icon";
import { Skeleton } from "./skeleton";

type AsyncStateKind = "loading" | "error" | "empty";

type AsyncStateProps = Readonly<{
  kind: AsyncStateKind;
  title: string;
  message: string;
  onRetry?: () => void;
  accessory?: ReactNode;
  /** The level of the title. A state inside a page sits under the page's `h1` (2, the default); a state that IS the page has no other `h1` (1). */
  headingLevel?: 1 | 2;
}>;

/**
 * What a page shows while it loads, when a read failed and when there is nothing to show. Loading is a few skeleton lines with the title and
 * the message in text (a screen reader reads those, politely); an error is announced at once and offers a retry.
 */
export function AsyncState({
  kind,
  title,
  message,
  onRetry,
  accessory,
  headingLevel = 2,
}: AsyncStateProps) {
  const Heading = headingLevel === 1 ? "h1" : "h2";
  const role = kind === "error" ? "alert" : "status";

  return (
    <section
      className={cn(
        "grid max-w-3xl gap-2 rounded-md border bg-card p-4 text-card-foreground shadow-sm sm:p-6",
        kind === "error" ? "border-destructive-border" : "border-border",
      )}
      role={role}
      aria-live={kind === "error" ? "assertive" : "polite"}
      aria-busy={kind === "loading" ? true : undefined}
    >
      {kind === "loading" ? (
        <div className="grid gap-2 pb-1" data-testid="async-skeleton">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
        </div>
      ) : null}
      <Heading className="m-0 flex items-center gap-2 text-xl leading-7 font-semibold tracking-tight">
        {kind === "error" ? (
          <Icon name="alert-02" className="size-5 shrink-0 text-destructive" />
        ) : kind === "empty" ? (
          <Icon name="inbox" className="size-5 shrink-0 text-muted-foreground" />
        ) : null}
        {title}
      </Heading>
      <p className="m-0 text-sm leading-6 text-muted-foreground">{message}</p>
      {accessory}
      {kind === "error" && onRetry ? (
        <div>
          <Button type="button" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </section>
  );
}
